import React, { Dispatch, FC, SetStateAction, useState } from "react";
import { Button, Form } from "react-bootstrap";
import {
  SolutionType,
  useCreateSolutionMutation,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { categories, industryTypes } from "../helpers/categories";
import Input from "../Builder/nodes/Logic/Inputs/Input";

type EditableSolution = SolutionType & {
  Monitor?: string;
};

interface CreateSolutionFormProps {
  solution: EditableSolution;
  setSolution: Dispatch<SetStateAction<EditableSolution>>;
  errors?: ErrorMap;
  setErrors?: Dispatch<SetStateAction<ErrorMap>>;
}

type ErrorMap = Record<string, string[] | null>;

const CreateSolutionForm: FC<CreateSolutionFormProps> = ({
  solution,
  setSolution,
  errors: propErrors,
  setErrors: propSetErrors,
}) => {
  const [createSolution] = useCreateSolutionMutation();
  const [internalErrors, setInternalErrors] = useState<ErrorMap>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<{ deployed: string; offset: number }>({
    deployed: "",
    offset: 0,
  });

  // Use passed errors if available, otherwise use internal state
  const errors = propErrors ?? internalErrors;
  const setErrors = propSetErrors ?? setInternalErrors;

  // Helper to get first error message from error array
  const getErrorMessage = (fieldErrors: string[] | null | undefined): string => {
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      // Extract just the error message, removing the field key prefix
      const errorMsg = fieldErrors[0];
      const match = errorMsg.match(/Error:(.+)/);
      return match ? match[1].trim() : errorMsg;
    }
    return "";
  };

  const saveSolution = (): void => {
    setLoading(true);
    createSolution(solution).then((res: any) => {
      if ("data" in res) {
        setSolution(res.data as EditableSolution);
        toast.success("Solution saved successfully");
      }
      if ("error" in res && res.error?.data?.errors) {
        setErrors(res.error.data.errors as ErrorMap);
      }
      setTimeout(() => {
        setLoading(false);
      }, 700);
    });
  };

  return (
    <div className="row mt-3">
      <div className="col-12 col-lg-6">
        <Form.Label className="fw-bold">Solution label: </Form.Label>
        <Form.Control
          type="text"
          placeholder="Solution Label"
          value={solution.Label}
          onChange={(e) => {
            setSolution({ ...solution, Label: e.target.value });
            setErrors({ ...errors, "Solution.Label": null });
          }}
          isInvalid={!!errors["Solution.Label"]}
          className="mb-3"
        />
        {errors["Solution.Label"] && (
          <div className="alert alert-danger p-1 mb-2">
            {getErrorMessage(errors["Solution.Label"])}
          </div>
        )}

        <Form.Label className="fw-bold">Customer sales description: </Form.Label>
        <Form.Control
          as="textarea"
          value={solution.Description}
          onChange={(e) => {
            setSolution({ ...solution, Description: e.target.value });
            setErrors({ ...errors, "Solution.Description": null });
          }}
          isInvalid={!!errors["Solution.Description"]}
          className="mb-3"
        />
        {errors["Solution.Description"] && (
          <div className="alert alert-danger p-1 mb-2">
            {getErrorMessage(errors["Solution.Description"])}
          </div>
        )}

        <Form.Label className="fw-bold">Reference: </Form.Label>
        <Form.Control
          type="text"
          placeholder="Solution Reference"
          value={solution.Reference}
          onChange={(e) => {
            setSolution({ ...solution, Reference: e.target.value });
            setErrors({ ...errors, "Solution.Reference": null });
          }}
          isInvalid={!!errors["Solution.Reference"]}
          className="mb-3"
        />
        {errors["Solution.Reference"] && (
          <div className="alert alert-danger p-1 mb-2">
            {getErrorMessage(errors["Solution.Reference"])}
          </div>
        )}

        <Form.Label className="fw-bold">Status: </Form.Label>
        <Form.Select
          className="mb-3"
          aria-label="status-select"
          onChange={(e) => {
            setSolution({
              ...solution,
              Status: e.target.value as SolutionType["Status"],
            });
            setErrors({ ...errors, "Solution.Status": null });
          }}
          value={solution.Status}
          isInvalid={!!errors["Solution.Status"]}
        >
          <option value="public">public</option>
          <option value="private">private</option>
          <option value="draft">draft</option>
        </Form.Select>
        {errors["Solution.Status"] && (
          <div className="alert alert-danger p-1 mb-2">
            {getErrorMessage(errors["Solution.Status"])}
          </div>
        )}

        <Form.Label className="fw-bold">Kind: </Form.Label>
        <Form.Select
          className="mb-3"
          aria-label="kind-select"
          onChange={(e) => {
            setSolution({ ...solution, Kind: e.target.value });
            setErrors({ ...errors, "Solution.Kind": null });
          }}
          value={solution.Kind}
          disabled={!!solution.Uuid}
          isInvalid={!!errors["Solution.Kind"]}
        >
          <option value="standard">Standard</option>
          <option value="events">Events</option>
        </Form.Select>
        {errors["Solution.Kind"] && (
          <div className="alert alert-danger p-1 mb-2">
            {getErrorMessage(errors["Solution.Kind"])}
          </div>
        )}
      </div>
      <div className="col-12 col-lg-6">
        <Form.Label className="fw-bold">Version: </Form.Label>
        <Form.Control
          type="text"
          placeholder="Solution Version"
          value={solution.Version}
          onChange={(e) => {
            setSolution({ ...solution, Version: e.target.value });
            setErrors({ ...errors, "Solution.Version": null });
          }}
          isInvalid={!!errors["Solution.Version"]}
          className="mb-3"
        />
        {errors["Solution.Version"] && (
          <div className="alert alert-danger p-1 mb-2">
           Version is required and must be in format "X.Y.Z". {getErrorMessage(errors["Solution.Version"])}
          </div>
        )}
        <div className="mb-2">
          <div className="d-flex justify-content-between">
            <Form.Label className="fw-bold">Industry: </Form.Label>
          </div>
          <div className=" bg-white rounded-3">
            <Input
              inputType="select"
              isMulti
              isValidNewOption={(o: string) => Boolean(o)}
              value={solution?.TagsMap?.industry || []}
              onChange={(values: string[]) =>
                setSolution((prev) => ({
                  ...prev,
                  TagsMap: {
                    ...(prev.TagsMap || { category: [], folder: [] }),
                    industry: values,
                  },
                }))
              }
              options={[
                {
                  value: "",
                  label: "Default from product settings",
                },
                ...industryTypes.map((c) => ({
                  value: c,
                  label: c,
                })),
              ]}
            />
          </div>
        </div>
        <div className="">
          <div className="d-flex justify-content-between">
            <Form.Label className="fw-bold">Category: </Form.Label>
          </div>{" "}
          <div className="bg-white rounded-3 mb-2">
            <Input
              inputType="select"
              isMulti
              isValidNewOption={(o: string) => Boolean(o)}
              value={solution?.TagsMap?.category || []}
              onChange={(values: string[]) =>
                setSolution((prev) => ({
                  ...prev,
                  TagsMap: {
                    ...(prev.TagsMap || { industry: [], folder: [] }),
                    category: values,
                  },
                }))
              }
              options={[
                {
                  value: "",
                  label: "Default from product settings",
                },
                ...categories.map((c) => ({
                  value: c,
                  label: c,
                })),
              ]}
            />
          </div>
          {!solution?.TagsMap?.category?.length && (
            <div className="alert alert-danger p-1 mb-2">
              Category is required
            </div>
          )}
        </div>
        <div className="mb-2">
          <Form.Label className="fw-bold">Business Use-Case: </Form.Label>

          <Form.Select
            aria-label="deployed-select"
            onChange={(e) => {
              setFilters({
                ...filters,
                deployed: e.target.value,
                offset: 0,
              });
            }}
          >
            <option value="yes">yes</option>
            <option value="no">no</option>
            <option value="">all</option>
          </Form.Select>
        </div>
      </div>
    </div>
  );
};

export default CreateSolutionForm;
